import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import AnnotToolbar from "./AnnotToolbar";
import AnnotOverlay from "./AnnotOverlay";
import AnnotList from "./AnnotList";
import { selectionRects, STAMPS } from "./annotUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const isImageName = (n) => /\.(jpe?g|png|gif|bmp|webp|tiff?|heic|heif)$/i.test(n || "");

/**
 * AnnotViewer – PDF-/Bild-Viewer mit Markierungstools (Textmarker, Rechteck, Stift, Kommentar, Stempel).
 * compact: schmale Darstellung (Vorschau-Panel), Liste standardmäßig zu.
 */
export default function AnnotViewer({ dokumentId, akteId, dokumentName, contentType, onClose, onMarkerChange, onAnnotChange, compact = false }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [isImage, setIsImage] = useState((contentType || "").startsWith("image/") || isImageName(dokumentName));
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [colWidth, setColWidth] = useState(0);
  const [pageSize, setPageSize] = useState({ w: 0, h: 0 });
  const [loadErr, setLoadErr] = useState(null);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#FACC15");
  const [stroke, setStroke] = useState(4);
  const [stamp, setStamp] = useState("wichtig");
  const [annots, setAnnots] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listOpen, setListOpen] = useState(!compact);
  const [exporting, setExporting] = useState(false);
  const colRef = useRef(null);
  const pageRef = useRef(null);

  // Dokument laden
  useEffect(() => {
    if (!dokumentId) return;
    let active = true; let url;
    setBlobUrl(null); setNumPages(0); setPage(1); setSelectedId(null); setLoadErr(null);
    (async () => {
      try {
        const r = await api.get(`/dokumente/${dokumentId}/download`, { responseType: "blob" });
        if (!active) return;
        const img = (r.data.type || "").startsWith("image/") || (contentType || "").startsWith("image/") || isImageName(dokumentName);
        setIsImage(img);
        if (img) setNumPages(1);
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      } catch (e) { if (active) setLoadErr(errMsg(e, "Dokument konnte nicht geladen werden")); }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [dokumentId, contentType, dokumentName]);

  // Markierungen laden
  useEffect(() => {
    if (!dokumentId) return;
    api.get(`/annotationen`, { params: { dokument_id: dokumentId } }).then(r => setAnnots(r.data)).catch(() => setAnnots([]));
  }, [dokumentId]);

  // Spaltenbreite → Seite passt sich an
  useEffect(() => {
    const el = colRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setColWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Gerenderte Seitengröße messen (für Overlay)
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setPageSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [blobUrl]);

  const fitWidth = colWidth > 0 ? Math.max(200, Math.min(colWidth - 32, 1100)) : 700;
  const renderWidth = fitWidth * scale;

  // Textmarker per Textauswahl (PDF)
  useEffect(() => {
    if (tool !== "highlight" || isImage) return;
    const handler = () => {
      if (!pageRef.current) return;
      const sel = selectionRects(pageRef.current);
      if (!sel) return;
      createAnnot({ typ: "highlight", page, farbe: color, rects: sel.rects, text: sel.text });
      window.getSelection()?.removeAllRanges();
    };
    document.addEventListener("mouseup", handler);
    document.addEventListener("touchend", handler);
    return () => { document.removeEventListener("mouseup", handler); document.removeEventListener("touchend", handler); };
    // eslint-disable-next-line
  }, [tool, isImage, page, color, dokumentId]);

  // Tastatur: Entf löscht Auswahl, Esc → Auswahl-Werkzeug
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) { e.preventDefault(); deleteAnnot(selectedId); }
      if (e.key === "Escape") { setSelectedId(null); setTool("select"); }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [selectedId]);

  const notify = useCallback((next) => { onAnnotChange?.(next); }, [onAnnotChange]);

  const createAnnot = async (data, opts = {}) => {
    const tmp = { ...data, id: `tmp-${Date.now()}`, dokument_id: dokumentId, akte_id: akteId };
    setAnnots(a => [...a, tmp]);
    try {
      const r = await api.post("/annotationen", { ...data, dokument_id: dokumentId });
      setAnnots(a => { const next = a.map(x => x.id === tmp.id ? r.data : x); notify(next); return next; });
      if (opts.openEditor || data.typ === "highlight") setSelectedId(r.data.id);
      if (data.typ === "pin" || data.typ === "stamp") setTool("select");
    } catch (e) {
      setAnnots(a => a.filter(x => x.id !== tmp.id));
      toast.error(errMsg(e, "Markierung konnte nicht gespeichert werden"));
    }
  };

  const updateAnnot = async (id, patch) => {
    setAnnots(a => a.map(x => x.id === id ? { ...x, ...patch } : x));
    try {
      const r = await api.patch(`/annotationen/${id}`, patch);
      setAnnots(a => a.map(x => x.id === id ? r.data : x));
      if ("schriftsatz_kategorie" in patch) { onMarkerChange?.(); toast.success(patch.schriftsatz_kategorie ? "Textmarker in Schriftsatz übernommen" : "Aus Schriftsatz entfernt"); }
    } catch (e) { toast.error(errMsg(e, "Änderung fehlgeschlagen")); }
  };

  const deleteAnnot = async (id) => {
    const a = annots.find(x => x.id === id);
    setAnnots(list => { const next = list.filter(x => x.id !== id); notify(next); return next; });
    setSelectedId(null);
    try { await api.delete(`/annotationen/${id}`); if (a?.marker_id) onMarkerChange?.(); }
    catch (e) { toast.error(errMsg(e, "Löschen fehlgeschlagen")); }
  };

  const exportAnnotiert = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/dokumente/${dokumentId}/annotiert`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${(dokumentName || "Dokument").replace(/\.[^.]+$/, "")}_markiert.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Markiertes PDF heruntergeladen");
    } catch (e) { toast.error(errMsg(e, "Export fehlgeschlagen")); }
    finally { setExporting(false); }
  };

  const pageAnnots = useMemo(() => annots.filter(a => a.page === page), [annots, page]);
  const jumpTo = (a) => { setPage(a.page); setSelectedId(a.id); };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white" data-testid="annot-viewer" data-doc={dokumentId}>
      <AnnotToolbar
        tool={tool} setTool={(t) => { setTool(t); setSelectedId(null); if (t === "stamp") setColor(STAMPS.find(s => s.key === stamp)?.color || color); }}
        color={color} setColor={setColor} stroke={stroke} setStroke={setStroke} stamp={stamp} setStamp={setStamp}
        page={page} numPages={numPages} setPage={(fn) => { setSelectedId(null); setPage(fn); }} scale={scale} setScale={setScale} onFit={() => setScale(1)}
        listOpen={listOpen} setListOpen={setListOpen} annotCount={annots.length} onExport={exportAnnotiert} exporting={exporting}
        onOpenTab={blobUrl ? () => window.open(blobUrl, "_blank") : null} onClose={onClose} compact={compact}
      />
      <div className="flex-1 flex min-h-0">
        <div ref={colRef} className="flex-1 overflow-auto bg-[#374151] p-3 sm:p-4 min-w-0 relative" data-testid="annot-canvas-col" onClick={() => tool === "select" && setSelectedId(null)}>
          {loadErr ? (
            <p className="text-red-200 text-sm text-center mt-16" data-testid="pdf-load-error">{loadErr}</p>
          ) : !blobUrl ? (
            <p className="text-white/70 text-sm text-center mt-16">Lädt Dokument …</p>
          ) : (
            <div className="w-max max-w-none mx-auto">
              <div ref={pageRef} className={`relative shadow-2xl bg-white ${tool === "highlight" && !isImage ? "annot-highlight-mode" : ""}`} data-testid="annot-page">
                {isImage ? (
                  <img src={blobUrl} alt={dokumentName || "Bild"} style={{ width: renderWidth, display: "block" }} draggable={false} data-testid="annot-image" />
                ) : (
                  <Document file={blobUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} onLoadError={(e) => setLoadErr(e?.message || String(e))}
                    loading={<div style={{ width: renderWidth, height: renderWidth * 1.41 }} className="flex items-center justify-center text-[#6B7280] text-sm">Lädt PDF …</div>}
                    error={<div style={{ width: renderWidth }} className="p-6 text-sm text-[#B91C1C]">PDF konnte nicht gerendert werden.</div>}>
                    <Page pageNumber={page} width={renderWidth} renderTextLayer renderAnnotationLayer={false} />
                  </Document>
                )}
                {pageSize.w > 0 && (
                  <AnnotOverlay annots={pageAnnots} pageSize={pageSize} page={page} tool={tool} color={color} stroke={stroke} stamp={stamp} isImage={isImage}
                    selectedId={selectedId} onSelect={setSelectedId} onCreate={createAnnot} onUpdate={updateAnnot} onDelete={deleteAnnot} />
                )}
              </div>
            </div>
          )}
        </div>
        {listOpen && <AnnotList annots={annots} page={page} selectedId={selectedId} onSelect={jumpTo} onDelete={deleteAnnot} />}
      </div>
    </div>
  );
}
